import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import seoService from './seo.service';
import type { MetaTag } from './seo.dto';
import { formatDateTime } from '@/helpers';

import { useToast } from '../../contexts/ToastContext';
const SEO = () => {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metaTags, setMetaTags] = useState<MetaTag[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMetaTags, setTotalMetaTags] = useState(0);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    metaTag: MetaTag | null;
  }>({
    open: false,
    metaTag: null,
  });

  const itemsPerPage = 10;

  useEffect(() => {
    seoService.setLogoutCallback?.(logout);
  }, [logout]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchMetaTags = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await seoService.getMetaTags(token, {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm || undefined,
      });

      setMetaTags(response.data || []);
      setTotalPages(response.meta?.lastPage || 1);
      setTotalMetaTags(response.meta?.total || 0);
    } catch (err) {
      console.error('Error fetching meta tags:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to fetch meta tags'
      );
      setMetaTags([]);
      setTotalPages(1);
      setTotalMetaTags(0);
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    fetchMetaTags();
  }, [fetchMetaTags]);

  const handleAddMetaTag = () => {
    navigate('/seo/add');
  };

  const handleEditMetaTag = (metaTagId: number) => {
    navigate(`/seo/edit/${metaTagId}`);
  };

  const handleDeleteClick = (metaTag: MetaTag) => {
    setDeleteDialog({ open: true, metaTag });
  };

  const handleDeleteConfirm = async () => {
    if (!token || !deleteDialog.metaTag?.id) return;

    try {
      await seoService.deleteMetaTag(token, deleteDialog.metaTag.id);

      showSuccess('Meta tag deleted successfully');

      // Refresh the list
      fetchMetaTags();
    } catch (err) {
      console.error('Error deleting meta tag:', err);
      showError(
        err instanceof Error ? err.message : 'Failed to delete meta tag'
      );
    } finally {
      setDeleteDialog({ open: false, metaTag: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, metaTag: null });
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading && metaTags.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          {/* <CodeIcon sx={{ mr: 1, fontSize: 32 }} color="primary" /> */}
          <Box>
            <Typography variant="h4">SEO Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage meta tags and SEO settings for your pages
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMetaTag}
          >
            Add Meta Tag
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search meta tags by page slug, title, or description..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: (
              <Box display="flex" alignItems="center" gap={1}>
                {searchTerm !== debouncedSearchTerm && (
                  <CircularProgress size={20} />
                )}
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    disabled={loading}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          }}
        />
      </Box>

      <Card>
        <CardContent>
          {metaTags.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No meta tags found
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Get started by creating your first meta tag
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddMetaTag}
              >
                Add First Meta Tag
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Page Slug</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Meta Title</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Meta Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Keywords</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Last Updated</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metaTags.map(metaTag => (
                    <TableRow key={metaTag.id} hover>
                      <TableCell>
                        <Chip
                          label={metaTag.page_slug}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {truncateText(metaTag.meta_title, 40)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {truncateText(metaTag.meta_description || '', 60)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {truncateText(metaTag.meta_keywords || '', 40)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(metaTag?.updatedAt || 'N/A')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditMetaTag(metaTag.id!)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(metaTag)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            p: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {loading
              ? 'Loading...'
              : `Showing page ${currentPage} of ${totalPages} (${totalMetaTags} meta tags)`}
          </Typography>
          <Box display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Meta Tag</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this meta tag?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SEO;
